import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Carregando from '@/components/Carregando';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { consolidarPorForma, ordenarPorDataCrescente } from '@/leads/consolidacao';
import {
  PISO_MES_DA_EQUIPE,
  chaveMes,
  mesPermitido,
  mesesVisiveis,
} from '@/metas/calculo';
import { temFuncao } from '@/equipe/funcoes';
import { ehVendedor } from '@/metas/credito';
import { consolidarFechamento, escolherExibicao } from '@/remuneracao/calculo';
import { ouvirAgendamentos } from '@/services/agendamentosService';
import {
  buscarMeuExtrato,
  buscarRecebimentosDoMes,
  ouvirConfigs,
  ouvirMinhaConfig,
} from '@/services/remuneracaoService';

/**
 * Extrato individual (VC).
 *
 * Cada pessoa vê **só o seu**. As rules garantem isso duas vezes: a config está
 * em `remuneracao/{uid}` e o extrato em `fechamentos/{mes}/pessoas/{uid}`, ambos
 * legíveis pelo gestor ou pelo dono do `uid`. Não há caminho pela tela para
 * alguém abrir o do colega.
 *
 * Mês FECHADO lê o snapshot; mês aberto calcula ao vivo e diz que é prévia. O
 * sinal de "fechado" é a EXISTÊNCIA do próprio documento — o cabeçalho do
 * fechamento traz os totais da folha e é só do gestor.
 */

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dia = (v) => {
  const d = typeof v?.toDate === 'function' ? v.toDate() : v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? format(d, 'dd/MM/yyyy') : '—';
};

export default function MeuExtratoPage() {
  const { clinicaId, usuario, isGestor, papel, perfil } = useAuth();
  const { leads, equipe, carregando: carregandoLeads } = useLeads();

  const [mes, setMes] = useState(() => chaveMes(new Date()));
  const [quem, setQuem] = useState(usuario?.uid ?? '');
  const [agendamentos, setAgendamentos] = useState([]);
  const [recebimentos, setRecebimentos] = useState(null);
  const [configs, setConfigs] = useState(null);
  const [snapshot, setSnapshot] = useState(undefined);
  const [erro, setErro] = useState(null);

  const alvo = isGestor ? quem : usuario?.uid;

  /**
   * ★★ WJ (29/set/2026) — O PORTÃO É A FUNÇÃO SDR, e ele mora AQUI.
   *
   * O extrato é de quem exerce a função de SDR. O negociador é remunerado pelo
   * resultado da clínica e não deve nem ver a seção (pedido do dono).
   *
   * ★★ POR QUE NÃO NO `RequireAuth`, que seria o lugar óbvio. Medido: `perfil`
   * nasce `null` e o `AuthProvider` usa `null` para DUAS coisas — "ainda não
   * carregou" e "não existe" (`AuthProvider.jsx:64`) —, e `carregando` cobre só
   * a sessão do Auth, não o perfil. No `RequireAuth` o `<Navigate to="/sem-acesso">`
   * (`RequireAuth.jsx:18-20`) JÁ TERIA ACONTECIDO quando o perfil chegasse: uma
   * SDR que desse F5 aqui seria expulsa da própria tela. Distinguir os dois
   * estados exigiria mexer no guarda de TODAS as rotas do app para atender UMA.
   * Decisão do dono (plano §0, saída 2): o portão fica na página.
   *
   * ★★ E POR QUE `Boolean(perfil) &&`: perfil AUSENTE não bloqueia. São dois
   * casos reais, não defensividade — (1) a janela até o snapshot chegar, e (2) o
   * SUPERADMIN, que não tem documento nesta clínica e está em `VENDAS`
   * (`App.jsx:33`). Falhar fechado trancaria os dois. (Mutação M7.)
   *
   * ★ `{ ...perfil, papel }` pelo mesmo motivo do menu: `funcoesDe` deriva do
   * papel quando `funcoes` está ausente — o caso da Jéssica e da Talita —, e a
   * autoridade sobre acesso é o CLAIM, não o campo do documento.
   */
  const semFuncaoSdr = Boolean(perfil) && !temFuncao({ ...perfil, papel }, 'sdr');

  /**
   * ★★ WG-c1 (28/set/2026) — O PISO DE MÊS DA EQUIPE.
   *
   * Pedido do dono: a SDR só vê o extrato **a partir de 2026-09**, porque nenhum
   * mês anterior foi fechado pelo sistema (medido: `fechamentos` com ZERO
   * documentos) e os números antigos são prévias sobre dados que ninguém
   * conferiu. **O gestor não tem piso** — ele precisa olhar para trás para
   * conferir.
   *
   * ★★ E O PISO VALE NA LEITURA, não só no seletor: `mesEfetivo` é o que
   * alimenta as TRÊS consultas e o cálculo da prévia. Se o estado chegasse a um
   * mês proibido por qualquer caminho, nada abaixo do piso seria nem pedido ao
   * banco. Guardar o mês inválido e corrigi-lo num efeito deixaria uma janela de
   * um render com os números à vista; derivar não deixa janela.
   */
  const piso = isGestor ? null : PISO_MES_DA_EQUIPE;
  const mesEfetivo = mesPermitido(mes, { piso });
  // ★ A lista vazia só acontece com piso no FUTURO (não alcançável hoje: o piso é
  // o mês corrente). Mesmo assim o seletor precisa conter o valor que exibe,
  // senão o controle fica num estado inválido.
  const mesesDoSeletor = mesesVisiveis(12, { piso });
  const opcoesDeMes = mesesDoSeletor.length ? mesesDoSeletor : [mesEfetivo];

  useEffect(() => {
    // ★ WJ: sem função de SDR não se pede NADA ao banco — o portão vem antes da
    // consulta, não depois. É o que o teste afirma (nenhuma chamada de leitura).
    if (!clinicaId || !usuario?.uid || semFuncaoSdr) return undefined;
    // O gestor lê a coleção inteira; o SDR só a própria — são regras diferentes,
    // e cada um chama a consulta que a sua permite.
    return isGestor
      ? ouvirConfigs(clinicaId, setConfigs, () => setErro('Não foi possível carregar as configurações.'))
      : ouvirMinhaConfig(clinicaId, usuario.uid, (c) => setConfigs(c ? { [usuario.uid]: c } : {}),
        () => setErro('Não foi possível carregar a sua configuração.'));
  }, [clinicaId, usuario?.uid, isGestor, semFuncaoSdr]);

  useEffect(() => {
    if (!clinicaId || semFuncaoSdr) return undefined;
    const [ano, m] = mesEfetivo.split('-').map(Number);
    return ouvirAgendamentos(
      clinicaId,
      { de: new Date(ano, m - 1, 1), ate: new Date(ano, m, 0, 23, 59, 59) },
      setAgendamentos,
      () => setErro('Não foi possível carregar os agendamentos.'),
    );
  }, [clinicaId, mesEfetivo, semFuncaoSdr]);

  useEffect(() => {
    if (!clinicaId || semFuncaoSdr) return;
    setRecebimentos(null);
    buscarRecebimentosDoMes(clinicaId, mesEfetivo).then(setRecebimentos).catch(() =>
      setErro('Não foi possível carregar os recebimentos do mês.'));
  }, [clinicaId, mesEfetivo, semFuncaoSdr]);

  useEffect(() => {
    if (!clinicaId || !alvo || semFuncaoSdr) return;
    setSnapshot(undefined);
    buscarMeuExtrato(clinicaId, mesEfetivo, alvo).then(setSnapshot).catch(() => setSnapshot(null));
  }, [clinicaId, mesEfetivo, alvo, semFuncaoSdr]);

  const previa = useMemo(() => {
    if (!configs || !recebimentos || !alvo) return null;
    const folha = consolidarFechamento({ leads, agendamentos, recebimentos, configs, equipe, mes: mesEfetivo });
    return folha.linhas.find((l) => l.id === alvo) ?? null;
  }, [leads, agendamentos, recebimentos, configs, equipe, mesEfetivo, alvo]);

  // ★★ WJ — a mensagem vem ANTES da espera: sem função de SDR nada é buscado, e
  // `snapshot`/`recebimentos` ficariam para sempre no estado inicial. Sem esta
  // ordem, o negociador veria um "Carregando…" eterno em vez da explicação.
  //
  // ★ É MENSAGEM, não `/sem-acesso`: o JOABE TEM acesso ao espaço Vendas inteiro
  // (`podeVendas` lê o papel, e ele é `sdr`). O que ele não tem é a FUNÇÃO que
  // faz esta tela significar alguma coisa. Uma expulsão diria a coisa errada, e
  // ele abriria chamado de um defeito que não existe.
  if (semFuncaoSdr) {
    return (
      <section className="pagina">
        <header className="pagina-cabecalho">
          <h1>Meu extrato</h1>
        </header>
        <p className="em-breve">
          O extrato é de quem exerce a função de SDR. A sua remuneração não é calculada aqui —
          fale com o gestor.
        </p>
      </section>
    );
  }

  if (carregandoLeads || snapshot === undefined || !recebimentos) return <Carregando />;

  // A escolha mora no módulo puro, e é testada lá: um mês fechado que voltasse a
  // recalcular mostraria um número diferente do que foi pago.
  const { linha, fechado } = escolherExibicao(snapshot, previa);

  /**
   * ★★ WG — ORDENAR E CONSOLIDAR NA LEITURA, e não no cálculo.
   *
   * O mês FECHADO não passa pelo cálculo do cliente: ele lê o snapshot de
   * `fechamentos/{mes}/pessoas/{uid}`, gravado por `fecharMes`
   * (`functions/remuneracao/callables.js:131`). Arrumar a ordem lá dentro
   * exigiria deploy de FUNCTIONS — travado, porque o repositório carrega o
   * `jidDoTelefone` do incidente de 24/set. Fazendo aqui, valem os dois
   * caminhos: prévia e snapshot. Ver `docs/wg-plano.md` §0.
   *
   * Medido em 28/set: `fechamentos` tinha 0 documentos — nenhum mês fechado
   * ainda. Setembro é o primeiro, e ele já nasce lido deste jeito.
   *
   * ⚠ E SEM `useMemo`: estes dois derivados nascem DEPOIS do `return` de
   * carregamento, e hook depois de early return quebra a regra dos hooks — o
   * teste de montagem pegou isto na primeira rodada ("Rendered more hooks than
   * during the previous render"). São listas de dezenas de itens; recalcular a
   * cada render não custa nada, e o código fica sem a armadilha.
   */
  const comparecimentos = ordenarPorDataCrescente(linha?.detalheComparecimentos ?? []);
  // ★ Uma linha por paciente + FORMA: 12 parcelas no cartão viram uma linha.
  // Medido em setembro: 228 linhas → 78 (a da Jéssica, 222 → 73).
  const recebimentosConsolidados = consolidarPorForma(linha?.detalheRecebimentos ?? []);

  return (
    <section className="pagina">
      <header className="pagina-cabecalho">
        <h1>Meu extrato — {mesEfetivo}</h1>
        <div className="linha">
          {isGestor && (
            <select value={quem} onChange={(e) => setQuem(e.target.value)}>
              <option value={usuario?.uid ?? ''}>Eu</option>
              {(equipe ?? []).filter(ehVendedor).map((p) => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
          )}
          <select value={mesEfetivo} onChange={(e) => setMes(e.target.value)} aria-label="Mês">
            {opcoesDeMes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}

      {!linha ? (
        <p className="em-breve">
          Nada a mostrar em {mesEfetivo}. Se a sua remuneração ainda não foi configurada, fale com o
          gestor — sem configuração, o mês não é calculado.
        </p>
      ) : (
        <>
          {fechado ? (
            <p className="aviso">
              <strong>Mês fechado</strong> — este é o valor que foi pago. Ele não muda mais,
              mesmo que um recebimento seja corrigido depois.
            </p>
          ) : (
            <p className="secao-sub">
              <strong>Prévia</strong> — o mês ainda não foi fechado, e os números mudam até lá.
            </p>
          )}

          <div className="indicadores">
            <div className="indicador">
              <span className="indicador-rotulo">Comparecimentos</span>
              <span className="indicador-valor">{linha.comparecimentos}</span>
              <span className="indicador-nota">{moeda(linha.valorFixo)} de fixo</span>
            </div>
            <div className="indicador">
              <span className="indicador-rotulo">Recebido no mês</span>
              <span className="indicador-valor">{moeda(linha.baseRecebida)}</span>
              <span className="indicador-nota">
                {linha.percentual ? `${String(linha.percentual).replace('.', ',')}% = ${moeda(linha.comissao)}` : 'sem percentual'}
              </span>
            </div>
            <div className="indicador">
              <span className="indicador-rotulo">Total</span>
              <span className="indicador-valor verde">{moeda(linha.total)}</span>
            </div>
          </div>

          <section className="secao">
            <h2 className="secao-titulo">Comparecimentos ({linha.comparecimentos})</h2>
            <p className="secao-sub">Cada paciente conta uma vez, no mês da primeira consulta.</p>
            <div className="tabela-envolve">
              <table className="tabela">
                <thead><tr><th>Paciente</th><th>Data</th></tr></thead>
                <tbody>
                  {comparecimentos.map((c) => (
                    <tr key={c.leadId}><td>{c.nome || '—'}</td><td>{dia(c.data)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="secao">
            <h2 className="secao-titulo">Recebimentos do mês</h2>
            <p className="secao-sub">
              O que o paciente <strong>pagou</strong> neste mês — inclusive de contratos
              fechados há meses.
            </p>
            <div className="tabela-envolve">
              <table className="tabela">
                {/* ★ WG: colunas SEPARADAS, não texto concatenado — dá para ler a
                    contagem de relance e o rodapé continua somando. */}
                <thead><tr><th>Paciente</th><th>Forma</th><th>Parcelas</th><th>Total</th></tr></thead>
                <tbody>
                  {recebimentosConsolidados.map((r) => (
                    <tr key={`${r.leadId}-${r.forma}`}>
                      <td>{r.leadNome || '—'}</td>
                      <td>{r.formaLabel}</td>
                      <td>{r.parcelas}</td>
                      <td className="valor">{moeda(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Total</th>
                    <th />
                    <th>{recebimentosConsolidados.reduce((s, r) => s + r.parcelas, 0)}</th>
                    <th>{moeda(linha.baseRecebida)}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>
        </>
      )}
    </section>
  );
}
